// 🔧 organizers.js
// ✅ Purpose: Super admin-only page to view all Organizations managed by the Organization Engine
// ✅ Features:
// - Loads organizations from /organizations
// - Displays organization statistics
// - Search organizations
// - Responsive Bootstrap layout
// - Pagination
// - Read-only Organization inspection
// - Future Health button placeholder
// - Future Backfill button placeholder
// - Uniform admin styling
// - Success/error notifications
// - Console logging for troubleshooting

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { Link, useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";

const AdminOrganizers = () => {

  //--------------------------------------------------------
  // State
  //--------------------------------------------------------

  const [organizations, setOrganizations] = useState([]);

  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState("");

  const [searchParams] = useSearchParams();

  const urlStatus =
    searchParams.get("status");

  const [searchTerm, setSearchTerm] =
    useState("");

  const [currentPage, setCurrentPage] =
    useState(1);

  const pageSize = 50;

  //--------------------------------------------------------
  // Auto clear notifications
  //--------------------------------------------------------

  useEffect(() => {

    if (!status) return;

    const timer =
      setTimeout(() => {

        setStatus("");

      }, 20000);

    return () => clearTimeout(timer);

  }, [status]);

  //--------------------------------------------------------
  // Reset paging when searching
  //--------------------------------------------------------

  useEffect(() => {

    setCurrentPage(1);

  }, [searchTerm]);

  //--------------------------------------------------------
  // Load organizations
  //--------------------------------------------------------

  const loadOrganizations =
    async () => {

      logger.log("Loading organizations...");

      setLoading(true);

      try {

        const snap =
          await getDocs(
            collection(db, "organizations")
          );

        const list =
          snap.docs
            .map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
            .sort((a, b) => {

              const getCreated = (record) => {

                const value =
                  record.createdAt || 0;

                if (value?.toDate) {
                  return value
                    .toDate()
                    .getTime();
                }

                return new Date(value)
                  .getTime();

              };

              return (
                getCreated(b) -
                getCreated(a)
              );

            });

        logger.log(
          "Organizations loaded:",
          list.length
        );

        setOrganizations(list);

      } catch (err) {

        logger.error(
          "Failed loading organizations",
          err
        );

        setStatus(
          "❌ Failed to load organizations."
        );

      } finally {

        setLoading(false);

      }

    };

  //--------------------------------------------------------
  // Initial load
  //--------------------------------------------------------

  useEffect(() => {

    loadOrganizations();

  }, []);

  //--------------------------------------------------------
  // Date formatting
  //--------------------------------------------------------

  const formatDate =
    (value) => {

      if (!value) return "—";

      try {

        const date =
          value.toDate
            ? value.toDate()
            : new Date(value);

        return date.toLocaleDateString();

      } catch {

        return "—";

      }

    };

  //--------------------------------------------------------
  // Search
  //--------------------------------------------------------

  const normalizedSearch =
    searchTerm
      .trim()
      .toLowerCase();

  const filteredOrganizations =
    organizations.filter(
      (organization) => {

        return [

          organization.name,

          organization.organizationSlug,

          organization.organizationType,

          organization.ownerUid,

        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);

      }
    );

  //--------------------------------------------------------
  // Pagination
  //--------------------------------------------------------

  const totalPages =
    Math.ceil(
      filteredOrganizations.length /
      pageSize
    );

  const paginatedOrganizations =
    filteredOrganizations.slice(

      (currentPage - 1) *
        pageSize,

      currentPage *
        pageSize

    );

  //--------------------------------------------------------
  // Loading
  //--------------------------------------------------------

  if (loading) {

    return (
      <FullPageSpinner
        message="Loading Organizations..."
      />
    );

  }

  //--------------------------------------------------------
  // Render
  //--------------------------------------------------------

  return (

    <section
      id="top-content"
      className="container-fluid py-5"
    >

      <div
        className="container"
        style={{
          maxWidth: "1400px",
        }}
      >

        <div className="row mb-4">

          <div className="col-12">

            <h1 className="display-5 mb-2">

              🏢 Organizers

            </h1>

            <p className="lead text-muted mb-0">

              View organizations created and
              managed by the Organization
              Engine.

            </p>

          </div>

        </div>

        {(status || urlStatus) && (

          <div className="alert alert-success mb-4">

            {status || urlStatus}

          </div>

        )}

        <div className="row g-4 mb-4">

          <div className="col-md-3">

            <div className="card shadow-sm">

              <div className="card-body">

                <div className="text-muted small">

                  Organizations

                </div>

                <div className="fs-3 fw-bold">

                  {organizations.length}

                </div>

              </div>

            </div>

          </div>

          <div className="col-md-3">

            <div className="card shadow-sm">

              <div className="card-body">

                <div className="text-muted small">

                  Default Organizations

                </div>

                <div className="fs-3 fw-bold">

                  {

                    organizations.filter(
                      (o) =>
                        o.isDefault ===
                        true
                    ).length

                  }

                </div>

              </div>

            </div>

          </div>

          <div className="col-md-3">

            <div className="card shadow-sm">

              <div className="card-body">

                <div className="text-muted small">

                  Active

                </div>

                <div className="fs-3 fw-bold">

                  {

                    organizations.filter(
                      (o) =>
                        o.status ===
                        "active"
                    ).length

                  }

                </div>

              </div>

            </div>

          </div>

          <div className="col-md-3">

            <div className="card shadow-sm">

              <div className="card-body">

                <div className="text-muted small">

                  Search Results

                </div>

                <div className="fs-3 fw-bold">

                  {
                    filteredOrganizations.length
                  }

                </div>

              </div>

            </div>

          </div>

        </div>

        <div className="card shadow-sm mb-4">

          <div className="card-header bg-white">

            <strong>

              Organization Administration

            </strong>

          </div>

          <div className="card-body">

            <div className="row g-3">

              <div className="col-lg-8">

                <label className="form-label">

                  Search Organizations

                </label>

                <input
                  className="form-control"
                  placeholder="Search by name, slug, type or owner..."
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(
                      e.target.value
                    )
                  }
                />

              </div>

              <div className="col-lg-4 d-flex align-items-end justify-content-lg-end gap-2">

                <button
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setSearchTerm("")
                  }
                >

                  Reset Search

                </button>

              </div>

            </div>

          </div>

        </div>

        <div className="card shadow-sm">

          <div className="card-header bg-white">

            <strong>

              Organizations

            </strong>

          </div>

          <div className="table-responsive">

            <table className="table table-hover align-middle mb-0">

              <thead className="table-light">

                <tr>

                  <th>Created</th>

                  <th>Name</th>

                  <th>Type</th>

                  <th>Owner</th>

                  <th>Status</th>

                  <th className="text-end">

                    Actions

                  </th>

                </tr>

              </thead>

              <tbody>

                {paginatedOrganizations.map(
                  (organization) => (

                    <tr
                      key={
                        organization.id
                      }
                    >

                      <td>

                        {formatDate(
                          organization.createdAt
                        )}

                      </td>

                      <td>

                        <div className="fw-bold">

                          {organization.name}

                        </div>

                        <div className="small text-muted">

                          {
                            organization.organizationSlug
                          }

                        </div>

                      </td>

                      <td>

                        {
                          organization.organizationType
                        }

                      </td>

                      <td>

                        {
                          organization.ownerUid ||
                          "—"
                        }

                      </td>

                      <td>

                        {
                          organization.status ||
                          "active"
                        }

                      </td>

                      <td className="text-end">

                        <div className="btn-group btn-group-sm">

                          <button
                            className="btn btn-outline-secondary"
                          >

                            View

                          </button>

                          <button
                            className="btn btn-outline-success"
                          >

                            Health

                          </button>

                        </div>

                      </td>

                    </tr>

                  )
                )}

              </tbody>

            </table>

          </div>

        </div>

      </div>

    </section>

  );

};

export default AdminOrganizers;